import { Body, breakpoints, Button, Dialog, Spacer } from '@spritz-finance/ui'
import React, { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { ADD_BILL_EVENT } from '../../../../analytics/events'
import { useAnalytics } from '../../../../context/useAnalytics'
import { ConnectAccountFAB } from './ConnectAccountFAB'

import { useNavigate } from 'react-router-dom'
import { GlassCard } from '../../../../components/Layout/GlassCard'
import { Row } from '../../../../components/Layout/Row'
import { AddAccountDialog } from '../../../offramp/components/addAccount/AddAccountDialog'
import { useEntityStatus } from '../useEntityStatus'

import { useIntercom } from 'react-use-intercom'
import AddBillsImage from '../../../../assets/icons/AddAccountIcon.svg'
import FindBillsImage from '../../../../assets/icons/Icon_Search@2x.png'
import PaySomeoneImage from '../../../../assets/icons/Pay someone@4x.png'
import { themedImage } from '../../../../theme/utils'
import { useIdentityVerification } from '../../../verification/useIdentityVerification'
import { BillPayStatus } from '../../../verification/VerificationContext'
import { TestPaymentGradientText } from '../../../../components/TestPaymentGradientText'
import { LinkBillDialog } from './LinkBillDialog'
import { Column } from '../../../../components/Layout/Column'
import { useTestPayAddBillsEligible } from './useTestPay'
import {
    TestPaymentLearnMoreWrapper,
    useTestPaymentInfoDialog,
} from '../../useTestPaymentInfoDialog'

interface LinkBillsProps {
    responsive?: boolean
    floating?: boolean
    additional?: boolean
    secondaryButton?: boolean
    autoOpen?: boolean
    foundBills?: boolean | null
    setFoundBills?: React.Dispatch<React.SetStateAction<boolean | null>>
    addBillOnly?: boolean
    hasExistingAccounts?: boolean
}

const DialogContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    max-height: 80vh;
    min-height: 195px;
    padding: 24px;
    overflow: auto;
    overflow-x: clip;
    max-width: 670px;
    ${breakpoints.med} {
        max-width: unset;
        padding: 16px;
    }
`

const FirstBillButton = styled.div<{ disabled?: boolean }>`
    box-sizing: border-box;
    border: 1px solid #595959;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 16px;
    min-width: 200px;
    height: 125px;
    transition: 0.25s box-shadow ease-in-out, 0.25s border-color ease-in-out,
        0.25s background-color ease-in-out;
    cursor: pointer;
    max-width: 310px;
    background: white;

    ${({ disabled }) =>
        disabled
            ? css`
                  opacity: 0.5;
                  cursor: inherit;
              `
            : css`
                  &:hover {
                      border: 1px solid #efefef;
                      box-shadow: 20px 20px 30px rgba(0, 0, 0, 0.1);
                      border-radius: 10px;
                  }
              `}

    ${breakpoints.xsmall} {
        margin-bottom: 16px;
        margin-right: 0px;
    }
`

const FirstBillImage = styled.img`
    height: 41px;
    //width: 55px;
    margin-bottom: 8px;
`

const AddInnerContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
`

const ButtonsWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    width: 100%;
    ${FirstBillButton} {
        flex-grow: 1;
    }

    ${breakpoints.xsmall} {
        flex-direction: column;
        ${FirstBillButton}:last-child {
            margin-bottom: 0px;
        }
    }
`

const ResponsiveSpacer = styled.div`
    height: 32px;
    ${breakpoints.xsmall} {
        height: 8px;
    }
`

const AddInnerContent: React.VFC<{
    onFindBills: () => void
    openAddBillDialog: () => void
    openPayOthersDialog: () => void
}> = ({ onFindBills, openAddBillDialog, openPayOthersDialog }) => {
    const { canAutoFindBills, canAddBills } = useEntityStatus()
    const isTestPayEligible = useTestPayAddBillsEligible()
    const { openDialog } = useTestPaymentInfoDialog()

    return (
        <AddInnerContentWrapper>
            {isTestPayEligible && (
                <>
                    <Body.Med size={16} align="center">
                        <TestPaymentGradientText>Get up to $10</TestPaymentGradientText>
                        towards your first payment on each new bill added within 7 days of passing
                        identity verification. &nbsp;
                        <TestPaymentLearnMoreWrapper onClick={openDialog}>
                            Learn more.
                        </TestPaymentLearnMoreWrapper>
                    </Body.Med>

                    <Spacer height={16} />
                </>
            )}

            <ButtonsWrapper>
                {canAutoFindBills && (
                    <>
                        <FirstBillButton onClick={onFindBills}>
                            <FirstBillImage src={themedImage('findBills', FindBillsImage)} />
                            <Body.SmBold>Let Spritz find your bills</Body.SmBold>
                        </FirstBillButton>
                        <Spacer width={16} />
                    </>
                )}
                {
                    <FirstBillButton
                        onClick={canAddBills ? openAddBillDialog : () => {}}
                        disabled={!canAddBills}
                    >
                        <FirstBillImage src={AddBillsImage} />
                        <Body.SmBold> Search for bills yourself</Body.SmBold>
                    </FirstBillButton>
                }
            </ButtonsWrapper>

            <ResponsiveSpacer />

            <Body.Bold size={22} align="center">
                or
            </Body.Bold>

            <ResponsiveSpacer />

            <Body.Med size={16} align="center">
                Send a crypto ACH bank transfer to anyone and easily pay your rent, invoices, or
                send fiat to friends and family.
            </Body.Med>

            <Spacer height={16} />

            <ButtonsWrapper>
                <FirstBillButton onClick={openPayOthersDialog}>
                    <FirstBillImage src={themedImage('paySomeone', PaySomeoneImage)} />
                    <Body.SmBold>Add a bank account</Body.SmBold>
                </FirstBillButton>
            </ButtonsWrapper>
        </AddInnerContentWrapper>
    )
}

export const LinkBills: React.VFC<LinkBillsProps> = ({
    floating = false,
    additional = false,
    foundBills = null,
    setFoundBills,
    hasExistingAccounts,
}) => {
    const navigate = useNavigate()

    const shouldOpenDialog = useRef(true)
    const { trackAnalyticsEvent } = useAnalytics()
    const { verificationStatus, billPayStatus } = useIdentityVerification()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [mainDialogOpen, setMainDialogOpen] = useState(false)
    const [payOthersDialogOpen, setPayOthersDialogOpen] = useState(false)

    const closeMainDialog = () => {
        setMainDialogOpen(false)
    }

    const openAddBillDialog = () => {
        setDialogOpen(true)
        closeMainDialog()
    }

    const handleCloseDialog = () => {
        setDialogOpen(false)
    }

    const handleAddBill = () => {
        trackAnalyticsEvent(ADD_BILL_EVENT, { additional })
        openAddBillDialog()
    }

    const onFindBills = () => {
        navigate('/find-my-bills', {})
    }

    const openPayOthersDialog = () => {
        setPayOthersDialogOpen(true)
        closeMainDialog()
    }

    useEffect(() => {
        if (!shouldOpenDialog.current) return
        if (foundBills !== null) {
            openAddBillDialog()
            setFoundBills?.(null)
            shouldOpenDialog.current = false
        }
    }, [foundBills, setFoundBills])

    const floatingContent = <ConnectAccountFAB onClick={() => setMainDialogOpen(true)} />

    const innerContentProps = {
        onFindBills,
        openAddBillDialog,
        openPayOthersDialog,
    }

    const firstTimeContent = (
        <GlassCard>
            <Spacer height={16} />
            <Row align="center">
                <Body.Bold size={22} align="center">
                    What would you like to do today?
                </Body.Bold>
            </Row>
            <Spacer height={32} />
            <AddInnerContent {...innerContentProps} />
        </GlassCard>
    )

    const existingAccountsContent = (
        <Row align="center" style={{ width: '100%' }}>
            <Button.CTA onClick={() => setMainDialogOpen(true)}>Add another account</Button.CTA>
        </Row>
    )

    const needsSSNContent = (
        <>
            <GlassCard>
                Attention: We are missing some data to fully verify your identity for the purposes
                of bill payment.
                <Spacer height={16} />
                <Row align="center">
                    <Button.CTA onClick={() => navigate('/verification')}>
                        Finish Verification
                    </Button.CTA>
                </Row>
            </GlassCard>
            <Spacer height={16} />
        </>
    )

    const { show: openIntercom } = useIntercom()
    const contactSupport = () => {
        openIntercom()
    }

    const setupErrorContent = (
        <>
            <GlassCard>
                Attention: There was an error setting up your Bill Payment access. Please contact
                support for more details.
                <Spacer height={16} />
                <Row align="center">
                    <Button.CTA onClick={contactSupport}>Contact Support</Button.CTA>
                </Row>
            </GlassCard>
            <Spacer height={16} />
        </>
    )

    const showFloatingContent = floating
    const showFirstTimeContent = !showFloatingContent && !hasExistingAccounts
    const showExistingAccounts = !showFloatingContent && hasExistingAccounts

    return (
        <>
            <Column>
                {(billPayStatus === BillPayStatus.NeedsSSN4 ||
                    billPayStatus === BillPayStatus.NeedsSSN9) &&
                    needsSSNContent}
                {(billPayStatus === BillPayStatus.SetupError ||
                    billPayStatus === BillPayStatus.Failed) &&
                    setupErrorContent}
                {showFloatingContent && floatingContent}
                {showFirstTimeContent && firstTimeContent}
                {showExistingAccounts && existingAccountsContent}
            </Column>
            <LinkBillDialog open={dialogOpen} onClose={handleCloseDialog} foundBills={foundBills} />
            <Dialog open={mainDialogOpen} onClose={() => setMainDialogOpen(false)}>
                <Dialog.Content contentPadding={false} minWidth={560}>
                    <Dialog.Title>Add account</Dialog.Title>
                    <DialogContentWrapper>
                        <AddInnerContent {...innerContentProps} />
                    </DialogContentWrapper>
                </Dialog.Content>
            </Dialog>
            <AddAccountDialog
                open={payOthersDialogOpen}
                onClose={() => setPayOthersDialogOpen(false)}
                payOthers
            />
        </>
    )
}
